import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import FetchModule from '../../Model/Share/Network/FetchModule';

import SubAccountRow from './SubAccountRow';
import SubAccountModal from './SubAccountModal';
import SubAccountDeleteModal from './SubAccountDeleteModal';

// 백엔드 SubAccountDetailDTO → 프론트 사용 형태로 변환 //
function mapSubAccountDto(dto) {
  return {
    id: dto.id,
    name: dto.name,
    loginId: dto.account,
    phone: dto.phone,
    countryCode: dto.countryCode,
    countryIso: dto.countryIso,
    profile: dto.profile,
    permissions: dto.role || [],
    isBilling: dto.isBilling,
    teacherId: dto.teacherId
  };
}

export default function SubAccountContent() {
  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [subAccounts, setSubAccounts] = useState([]);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);

  const fetchSubAccounts = useCallback(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('sub-account/', 'GET');

        if (responseData.status === 200) setSubAccounts((responseData.data || []).map(mapSubAccountDto));
        else if (responseData.status === 401) history.push('/');
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }, [history, t]);

  useEffect(() => {
    fetchSubAccounts();
  }, [fetchSubAccounts]);

  function onClickAdd() {
    setIsAddOpen(true);
  }

  function onClickEditSub(sub) {
    setSelectedSub(sub);
    setIsEditOpen(true);
  }

  function onClickDeleteSub(sub) {
    setSelectedSub(sub);
    setIsDeleteOpen(true);
  }

  return (
    <TopLevelWrapper>
      <RowList scrollBarColor={theme.scrollBar}>
        {
          subAccounts.length === 0 ?
            <EmptyWrapper>
              <EmptyText color={theme.line}>{t('myStoreSetting.account.sub.empty')}</EmptyText>
            </EmptyWrapper>
            :
            subAccounts.map(sub => (
              <SubAccountRow
                key={'SubAccountRow-' + sub.id}
                data={sub}
                onClickEdit={() => onClickEditSub(sub)}
                onClickDelete={() => onClickDeleteSub(sub)} />
            ))
        }
      </RowList>
      <AddButton borderColor={theme.primary} onClick={onClickAdd}>
        <AddButtonText color={theme.primary}>{t('myStoreSetting.account.sub.addButton')}</AddButtonText>
      </AddButton>
      {
        isAddOpen &&
        <SubAccountModal
          isNew={true}
          onClickExit={() => setIsAddOpen(false)}
          onSuccess={fetchSubAccounts} />
      }
      {
        isEditOpen &&
        <SubAccountModal
          isNew={false}
          initialData={selectedSub}
          onClickExit={() => setIsEditOpen(false)}
          onSuccess={fetchSubAccounts} />
      }
      {
        isDeleteOpen &&
        <SubAccountDeleteModal
          data={selectedSub}
          onClickExit={() => setIsDeleteOpen(false)}
          onSuccess={fetchSubAccounts} />
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  width: 100%;
  height: 402px;

  padding: 20px 10px;

  gap: 20px;

  border-radius: 10px;  
  background-color: ${props => props.theme.subBackground};
`;

const RowList = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: stretch;

  flex: 1 1 0;
  width: 100%;

  overflow-y: auto;

  scrollbar-width: thin;
  scrollbar-color: ${props => props.scrollBarColor} transparent;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.scrollBarColor};

    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    margin: 4px 0;

    background-color: transparent;

    border-radius: 300px;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: transparent;
  }
`;

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 1 0;
  width: 100%;
`;

const EmptyText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;

const AddButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 60px;

  border-radius: 10px;
  border: 1px dashed ${props => props.borderColor};

  flex-shrink: 0;
  margin-top: 20px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const AddButtonText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  color: ${props => props.color};

  margin: 0 0 0 0;
`;
